import { useEffect, useState } from 'react'
import { Navigate, useNavigate } from 'react-router-dom'
import { initializeSession, mustChangePassword } from '../../lib/auth'
import { type Lang } from '../../lib/i18n'
import { changeOwnPassword, StaffApiError } from '../../lib/staff-api'
import { st } from './staff-i18n'
import { PasswordInput } from '../../components/PasswordInput'
import { CommandHeader } from '../../components/CommandHeader'

interface Props {
  lang: Lang
  onLangToggle: () => void
  isOnline: boolean
}

type AuthState = 'checking' | 'ok' | 'login' | 'cockpit'

// R413 (c). The owner set this person's password, so the master layer refuses everything
// (403 PASSWORD_CHANGE_REQUIRED) until they choose their own. Same form and validation as
// AccountScreen. A successful change kills the current token, so the person goes back to
// /login and the next sign-in comes back with the flag cleared.
export function MustChangeScreen({ lang, onLangToggle, isOnline }: Props) {
  const navigate = useNavigate()
  const [authState, setAuthState] = useState<AuthState>('checking')
  const [current, setCurrent] = useState('')
  const [next, setNext] = useState('')
  const [again, setAgain] = useState('')
  const [submitting, setSubmitting] = useState(false)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    initializeSession().then((session) => {
      if (!session.authenticated) setAuthState('login')
      else setAuthState(mustChangePassword() ? 'ok' : 'cockpit')
    })
  }, [])

  if (authState === 'checking') return null
  if (authState === 'login') return <Navigate to="/login" replace />
  if (authState === 'cockpit') return <Navigate to="/cockpit" replace />

  const dir = lang === 'ar' ? 'rtl' : 'ltr'

  const canSubmit =
    next.length >= 8 && next === again && next !== current && !submitting

  function failureMessageFor(err: unknown): string {
    if (err instanceof StaffApiError) {
      if (err.status === 403) return st(lang, 'accountError403')
      if (err.status === 429) return st(lang, 'accountError429')
      return st(lang, 'accountErrorOther')
    }
    // A thrown non-StaffApiError is a network failure: the offline case.
    return st(lang, 'accountErrorOffline')
  }

  async function submit() {
    setSubmitting(true)
    setError(null)
    try {
      await changeOwnPassword(current, next)
      // The token that made this call is dead now; sign in again with the new password.
      navigate('/login', { replace: true })
    } catch (err) {
      // Failure: the inputs stay, only the pending flag clears and an error appears.
      setSubmitting(false)
      setError(failureMessageFor(err))
    }
  }

  return (
    <div dir={dir} className="min-h-screen bg-background font-sans">
      {/* R492: the shell, but no Back and no way home: the password change is forced. */}
      <div className="sticky top-0 z-10 shadow">
        <CommandHeader
          title={st(lang, 'mustChangeTitle')}
          titleTestId="must-change-title"
          lang={lang}
          onLangToggle={onLangToggle}
          isOnline={isOnline}
          showHome={false}
          showBack={false}
        />
      </div>

      <main className="px-4 py-6 max-w-sm mx-auto pb-10">
        <p className="text-sm text-foreground mb-4" data-testid="must-change-body">
          {st(lang, 'mustChangeBody')}
        </p>
        <form
          className="space-y-3"
          data-testid="must-change-form"
          onSubmit={(e) => {
            e.preventDefault()
            if (canSubmit) void submit()
          }}
        >
          {error && (
            <div
              className="rounded-md bg-destructive/10 border border-destructive/30 px-3 py-2 text-sm text-destructive"
              data-testid="must-change-error"
            >
              {error}
            </div>
          )}

          <div>
            <label className="block text-sm font-medium text-foreground mb-1">
              {st(lang, 'accountFieldCurrent')}
            </label>
            <PasswordInput
              lang={lang}
              autoComplete="current-password"
              data-testid="must-change-current-password"
              value={current}
              onChange={(e) => setCurrent(e.target.value)}
              disabled={!isOnline}
              className="w-full rounded-md border border-input px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-ring"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-foreground mb-1">
              {st(lang, 'accountFieldNew')}
            </label>
            <PasswordInput
              lang={lang}
              autoComplete="new-password"
              data-testid="must-change-new-password"
              value={next}
              onChange={(e) => setNext(e.target.value)}
              disabled={!isOnline}
              className="w-full rounded-md border border-input px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-ring"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-foreground mb-1">
              {st(lang, 'accountFieldConfirm')}
            </label>
            <PasswordInput
              lang={lang}
              autoComplete="new-password"
              data-testid="must-change-confirm-password"
              value={again}
              onChange={(e) => setAgain(e.target.value)}
              disabled={!isOnline}
              className="w-full rounded-md border border-input px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-ring"
            />
          </div>

          {!isOnline && (
            <p className="text-xs text-amber-600" data-testid="must-change-offline">
              {st(lang, 'accountErrorOffline')}
            </p>
          )}

          <button
            type="submit"
            disabled={!canSubmit || !isOnline}
            data-testid="must-change-submit"
            className="w-full rounded-md bg-primary px-4 py-2.5 text-sm font-semibold text-primary-foreground hover:bg-primary/90 disabled:opacity-50 transition-colors min-h-[44px]"
          >
            {submitting ? st(lang, 'accountSubmitting') : st(lang, 'accountSubmit')}
          </button>
        </form>
      </main>
    </div>
  )
}
